/**
 * League reminders by Telegram, email and calendar (docs/game-design.md §7.4, D56) for the menus:
 * whether the bot is there and this player's Telegram is connected, whether emails are on, and the
 * calendar link, shared by Settings and the fight cards. Connecting happens in Telegram itself, so after Connect the status
 * is asked again until it is (and whenever the game comes back on screen).
 */
import { create } from 'zustand';
import type { CalendarStatus, EmailStatus, TelegramStatus } from '../net/remindersApi';
import { diag } from '../platform/diag';

/** How long, and how often, to ask whether the player pressed Start in Telegram. */
const WATCH_MS = 5 * 60_000;
const WATCH_EVERY_MS = 3000;

export interface RemindersState {
  /** Null until known (and for guests, who have no league). */
  telegram: TelegramStatus | null;
  email: EmailStatus | null;
  calendar: CalendarStatus | null;
  load(): Promise<void>;
  /** The league calendar link, made now if needed; false when it couldn't be. */
  makeCalendar(language: string): Promise<boolean>;
  removeCalendar(): Promise<boolean>;
  /** False when it couldn't be saved. */
  setEmail(on: boolean, language: string): Promise<boolean>;
  /** After Connect: asks again until the player pressed Start in Telegram. */
  watchTelegram(): void;
  /** False when it couldn't be done. */
  disconnectTelegram(): Promise<boolean>;
}

let watching: ReturnType<typeof setInterval> | null = null;

export const useReminders = create<RemindersState>((set, get) => ({
  telegram: null,
  email: null,
  calendar: null,

  load: async () => {
    try {
      const { fetchReminders } = await import('../net/remindersApi');
      const status = await fetchReminders();
      set({ telegram: status.telegram, email: status.email ?? null, calendar: status.calendar ?? null });
    } catch (err) {
      diag.error(`reminders: ${String(err)}`);
    }
  },

  makeCalendar: async (language) => {
    try {
      const { calendarLinkRemote } = await import('../net/remindersApi');
      const url = await calendarLinkRemote(language);
      set({ calendar: { available: true, url } });
      return true;
    } catch (err) {
      diag.error(`reminders: calendar link failed: ${String(err)}`);
      return false;
    }
  },

  removeCalendar: async () => {
    try {
      const { removeCalendarRemote } = await import('../net/remindersApi');
      await removeCalendarRemote();
      set({ calendar: { available: true, url: null } });
      return true;
    } catch (err) {
      diag.error(`reminders: calendar remove failed: ${String(err)}`);
      return false;
    }
  },

  setEmail: async (on, language) => {
    try {
      const { setEmailRemindersRemote } = await import('../net/remindersApi');
      set({ email: await setEmailRemindersRemote(on, language) });
      return true;
    } catch (err) {
      diag.error(`reminders: email ${on ? 'on' : 'off'} failed: ${String(err)}`);
      return false;
    }
  },

  watchTelegram: () => {
    if (watching) clearInterval(watching);
    const until = Date.now() + WATCH_MS;
    const tick = () => {
      if (get().telegram?.linked || Date.now() > until) {
        if (watching) clearInterval(watching);
        watching = null;
        return;
      }
      if (!document.hidden) void get().load();
    };
    watching = setInterval(tick, WATCH_EVERY_MS);
  },

  disconnectTelegram: async () => {
    try {
      const { telegramUnlinkRemote } = await import('../net/remindersApi');
      await telegramUnlinkRemote();
      const t = get().telegram;
      set({ telegram: t ? { ...t, linked: false } : t });
      return true;
    } catch (err) {
      diag.error(`reminders: disconnect failed: ${String(err)}`);
      return false;
    }
  },
}));

// Back from Telegram: the player may have pressed Start.
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    const t = useReminders.getState().telegram;
    if (!document.hidden && watching && t && !t.linked) void useReminders.getState().load();
  });
}
